import { Upload } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { trackStorageEvent } from '../services/storageAnalytics';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { toast } from '../../../../components/ui/toaster';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';
import type { StorageToolbarContext } from '../toolbar/toolbarExtensions';
import type { StorageResourceRef } from '../../../../types/azureStorage';
import { blobViewOfHnsPath } from '../../../../core/engines/azureStorageHnsPaths';
import { requestStorageUpload } from './storageMutations';

type UploadDestination = Extract<StorageResourceRef, { kind: 'container' | 'blobPrefix' }>;

export function uploadDestination(ctx: Pick<StorageToolbarContext, 'resource'>): UploadDestination | undefined {
  // S8: a Data Lake location uploads through the Blob API into its directory's prefix (multi-protocol on HNS).
  if (ctx.resource.kind === 'hnsPath' && ctx.resource.isDirectory) return blobViewOfHnsPath(ctx.resource) as UploadDestination;
  return ctx.resource.kind === 'container' || ctx.resource.kind === 'blobPrefix' ? ctx.resource : undefined;
}

async function startUpload(connectionId: string, destination: UploadDestination, sourceKind: 'files' | 'folder', source: 'toolbar' | 'empty-state'): Promise<void> {
  try {
    // The job's own surface bucket stays 'toolbar' (the empty state's button is the toolbar's action); the empty state
    // fires its own event.
    await requestStorageUpload({ connectionId, destination, sourceKind, source: 'toolbar' });
    if (source === 'empty-state') trackStorageEvent('feature:storage_empty_upload', { sourceKind });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Upload could not be started.';
    toast.error({ title: 'Upload failed', description: message });
  }
}

/** U33: the empty blob / Data Lake location's own Upload (files; folders keep the toolbar menu and drops). */
export function uploadFromEmptyState(connectionId: string, resource: StorageResourceRef): Promise<void> {
  const destination = uploadDestination({ resource });
  return destination ? startUpload(connectionId, destination, 'files', 'empty-state') : Promise.resolve();
}

/** The toolbar's Upload menu; unavailable (read-only, no destination, refused data access — A9 / U12) it is a ReasonButton. */
export function StorageUploadMenu({ ctx }: { ctx: StorageToolbarContext }) {
  const destination = uploadDestination(ctx);
  const writeRefusal = typeof ctx.capabilities.writeRefusal === 'string' ? ctx.capabilities.writeRefusal : undefined;
  const uploadRefusal = typeof ctx.capabilities.uploadRefusal === 'string' ? ctx.capabilities.uploadRefusal : undefined;
  const reason = ctx.readOnly ? STORAGE_READ_ONLY_REASON : !destination ? 'Open a container or folder to upload into it' : writeRefusal ?? uploadRefusal;
  const upload = (sourceKind: 'files' | 'folder') => { if (destination && !reason) void startUpload(ctx.connectionId, destination, sourceKind, 'toolbar'); };
  if (reason) return <ReasonButton variant="outline" size="xs" className="gap-1" reason={reason} data-testid="storage-toolbar-upload"><Upload className="h-3.5 w-3.5" /> Upload</ReasonButton>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="xs" className="gap-1" title="Upload files or a folder" data-testid="storage-toolbar-upload">
          <Upload className="h-3.5 w-3.5" /> Upload
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => upload('files')} data-testid="storage-upload-files">Upload files…</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => upload('folder')} data-testid="storage-upload-folder">Upload folder…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
